﻿// @section: layout-shell
import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Home, Sparkles, BarChart3,
  Bell, Search, ChevronRight, ChevronDown, HelpCircle, Zap,
  LogOut, User, Bot, X,
  Plug, Shield, Crown,
  Target, MessageSquare, Database, ClipboardCheck, Megaphone, UsersRound, Palette, SearchCheck,
  Settings, BriefcaseBusiness, Building2, Video, FileText, Package, SlidersHorizontal,
  Workflow,
  type LucideIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LiangmuBrandMark } from "@/components/LiangmuBrandLogo";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api";
import { defaultSystemSettings, loadSystemSettings, saveSystemSettings, SYSTEM_SETTINGS_UPDATED, type SystemSettings } from "@/lib/system-settings";

export const PLATFORM_UI_CONTRACT_VERSION = "2026-08-17-consolidated";

const shell = {
  page: "#0f1419",
  panel: "#1a2332",
  panel2: "#242d3d",
  border: "#2d3a4f",
  muted: "#6b7280",
  primary: "#0d7046",
  primaryHover: "#0a5c3a",
};

const BUILDER_DOMAIN_DRAFT_STORAGE_KEY = "seo-nexus-builder-domain-draft";
const SITE_BUILDER_RESET_EVENT = "seo-nexus-site-builder-reset";

type SidebarNavItem = {
  path: string;
  label: string;
  icon: LucideIcon;
  badge?: string | null;
  aliases?: string[];
  indent?: boolean;
  adminOnly?: boolean;
  trailingChevron?: boolean;
};

type NextNavSection = {
  id: string;
  label: string;
  icon: LucideIcon;
  items: SidebarNavItem[];
};

type GlobalSearchItem = {
  id: string;
  label: string;
  desc: string;
  path: string;
  group: string;
  icon: LucideIcon;
  keywords: string;
};

const globalSearchMeta: Record<string, { desc: string; keywords: string }> = {
  工作台: { desc: "查看今日运营、站点异常、任务进度和快捷入口", keywords: "首页 工作台 今日 运营 预警 快捷入口" },
  工作流协助: { desc: "创建、执行和跟踪跨站点自动化工作流", keywords: "工作流 自动化 内容规划 建站 执行记录" },
  客户管理: { desc: "管理客户资料、品牌接入、企业知识库和GEO服务信息", keywords: "客户 品牌 企业知识库 资料 GEO 客户中心" },
  代理管理: { desc: "管理代理账号、等级、权限、客户归属和业绩数据", keywords: "代理 代理级别 权限 招商 渠道" },
  项目管理: { desc: "查看项目进度、客户站点、负责人和增长任务", keywords: "项目 进度 负责人 客户项目" },
  团队协作管理: { desc: "管理员工任务、项目协作、成员工作和团队日报", keywords: "任务 员工 团队 协作 日报 成员" },
  建站中心: { desc: "部署WordPress站点、模板建站、服务器与宝塔配置", keywords: "建站 WordPress 宝塔 模板 部署 站点" },
  站群管理: { desc: "管理已添加站点、域名、服务器、SEO部署和站点异常", keywords: "站点 站群 域名 服务器 宝塔 WordPress 异常" },
  SEO中心: { desc: "进入SEO首页、诊断、关键词、收录、流量和竞品分析", keywords: "SEO 诊断 关键词 收录 流量 竞品" },
  SEO诊断: { desc: "诊断网站TDK、收录、结构、速度和搜索引擎问题", keywords: "SEO诊断 TDK 收录 Sitemap 网站检测" },
  GEO中心: { desc: "执行GEO诊断、Prompt问题库、AI答案监测和报告", keywords: "GEO 诊断 Prompt AI答案 品牌可见度 报告" },
  GEO诊断: { desc: "生成企业或网站GEO报告，分析AI答案可见度", keywords: "GEO诊断 GEO报告 企业诊断 网站诊断 AI可见度" },
  关键词中心: { desc: "拓词、洗词、生成标题并分发到文章生产", keywords: "关键词 拓词 洗词 标题 文章标题" },
  发布中心: { desc: "管理多渠道发布、媒体信源、自媒体账号和发布任务", keywords: "发布 媒体 信源 自媒体 账号" },
  报告中心: { desc: "查看客户报告、SEO/GEO报告、收录和流量数据", keywords: "报告 数据 统计 客户报告 可见度" },
  AI设计中心: { desc: "生成营销素材、图片创意和视觉设计内容", keywords: "AI设计 图片 设计 素材 营销" },
  AI视频创作: { desc: "制作视频脚本、短视频素材和营销视频内容", keywords: "视频 短视频 创作 营销工具" },
  提示词中心: { desc: "管理提示词模板、行业Prompt和AI调用规则", keywords: "提示词 Prompt 模板 规则" },
  文章创作: { desc: "进入文章创作和内容生产任务", keywords: "文章 爆文 创作 内容 标题" },
  套餐与权限: { desc: "查看系统套餐、权限矩阵和增值服务", keywords: "套餐 权限 价格 版本" },
  API与集成: { desc: "配置大模型、百度、搜索引擎和第三方接口", keywords: "API 接口 集成 DeepSeek 豆包 Gemini 百度" },
  系统设置: { desc: "配置系统策略、AI模型、内容规则和安全审计", keywords: "系统设置 AI模型 内容规则 安全审计" },
};

const nextHomeItem: SidebarNavItem = { path: "/workbench", label: "工作台", icon: Home };

const nextNavSections: NextNavSection[] = [
  {
    id: "customer",
    label: "管理中心",
    icon: Settings,
    items: [
      { path: "/geo-customers", label: "客户管理", icon: UsersRound },
      { path: "/agent-management", label: "代理管理", icon: BriefcaseBusiness },
      { path: "/project-management", label: "项目管理", icon: Building2 },
      { path: "/team-collaboration", label: "团队协作管理", icon: UsersRound, aliases: ["/team-collaboration/tasks", "/team-collaboration/projects", "/team-collaboration/members", "/team-collaboration/logs", "/team-collaboration/reports"] },
    ],
  },
  {
    id: "growth",
    label: "增长中心",
    icon: Target,
    items: [
      { path: "/site-builder", label: "建站中心", icon: MessageSquare, aliases: ["/site-build-pipeline"] },
      { path: "/dashboard", label: "SEO中心", icon: SearchCheck, aliases: ["/analytics", "/keywords", "/traffic", "/competitor", "/indexing"] },
      { path: "/geo-center", label: "GEO中心", icon: Target, aliases: ["/geo-diagnosis", "/geo-prompts", "/geo-ai-answers", "/geo-competitors", "/geo-sources", "/geo-content-gap", "/geo-content-optimization", "/geo-tracking", "/geo-iteration", "/geo-assets"] },
      { path: "/geo-media", label: "发布中心", icon: Megaphone, aliases: ["/media-sessions"] },
      { path: "/reports", label: "报告中心", icon: BarChart3, aliases: ["/report-center"] },
    ],
  },
  {
    id: "tools",
    label: "AI工具",
    icon: Sparkles,
    items: [
      { path: "/ai-design-center", label: "AI设计中心", icon: Palette, badge: "NEW", trailingChevron: true },
      { path: "/marketing-tools", label: "AI视频创作", icon: Video, badge: "NEW", trailingChevron: true },
      { path: "/prompt-center", label: "提示词中心", icon: FileText, trailingChevron: true },
      { path: "/geo-tasks", label: "文章创作", icon: ClipboardCheck, badge: "10" },
    ],
  },
  {
    id: "system",
    label: "系统管理",
    icon: SlidersHorizontal,
    items: [
      { path: "/pricing", label: "套餐与权限", icon: Package },
      { path: "/api-center", label: "API与集成", icon: Plug },
      { path: "/system-settings", label: "系统设置", icon: Shield },
    ],
  },
];

const nextStandaloneItems: SidebarNavItem[] = [
  { path: "/workflows", label: "工作流协助", icon: Workflow },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const [openNextSections, setOpenNextSections] = useState<Record<string, boolean>>({
    customer: true,
    growth: true,
    tools: true,
    system: true,
  });
  const [systemSettings, setSystemSettings] = useState<SystemSettings>(() => loadSystemSettings());
  const [globalSearch, setGlobalSearch] = useState("");
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const globalSearchRef = useRef<HTMLDivElement | null>(null);
  const globalSearchInputRef = useRef<HTMLInputElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const userInitial = user?.name ? user.name[0].toUpperCase() : "U";
  const userName = user?.name ?? "用户";
  const isAdmin = user?.role === "admin";
  const activeShell = shell;
  const isDark = true;
  const sidebarWidth = collapsed ? 68 : 248;
  const sidebarPanel = activeShell.panel;
  const sidebarBorder = activeShell.border;
  const sidebarPrimaryText = "#ffffff";
  const hasCustomPlatformLogo = Boolean(systemSettings.logoDataUrl && systemSettings.logoDataUrl !== defaultSystemSettings.logoDataUrl);

  const isRouteActive = (path: string, aliases?: string[]) => {
    const matchesTarget = (target: string) => {
      const [targetPath, targetQuery = ""] = target.split("?");
      const targetSearch = targetQuery ? `?${targetQuery}` : "";
      if (targetSearch) return location.pathname === targetPath && location.search === targetSearch;
      return location.pathname === targetPath || location.pathname.startsWith(`${targetPath}/`);
    };
    if (matchesTarget(path)) return true;
    return Boolean(aliases?.some(alias => matchesTarget(alias)));
  };

  const toggleNextSection = (sectionId: string) => {
    setOpenNextSections(previous => ({ ...previous, [sectionId]: !previous[sectionId] }));
  };

  const nextItemsForCollapsed = [
    nextHomeItem,
    ...nextStandaloneItems,
    ...nextNavSections.flatMap(section => section.items),
  ].filter(item => !item.adminOnly || isAdmin);

  const globalSearchTargets = useMemo<GlobalSearchItem[]>(() => {
    const extraEntries: Array<{ item: SidebarNavItem; group: string }> = [
      { item: { path: "/sites", label: "站群管理", icon: SearchCheck }, group: "增长中心" },
      { item: { path: "/keyword-center", label: "关键词中心", icon: Target }, group: "增长中心" },
      { item: { path: "/seo-diagnosis", label: "SEO诊断", icon: SearchCheck }, group: "增长中心" },
      { item: { path: "/geo-diagnosis", label: "GEO诊断", icon: Target }, group: "增长中心" },
    ];
    const sectionEntries = nextNavSections.flatMap(section => (
      section.items
        .filter(item => !item.adminOnly || isAdmin)
        .map(item => ({ item, group: section.label }))
    ));
    const allEntries = [
      { item: nextHomeItem, group: "首页" },
      ...nextStandaloneItems.filter(item => !item.adminOnly || isAdmin).map(item => ({ item, group: "常用入口" })),
      ...sectionEntries,
      ...extraEntries,
    ];
    const seen = new Set<string>();
    return allEntries
      .filter(({ item }) => {
        const key = `${item.path}-${item.label}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map(({ item, group }) => {
        const meta = globalSearchMeta[item.label] || { desc: `打开${item.label}`, keywords: item.label };
        return {
          id: `${group}-${item.path}-${item.label}`,
          label: item.label,
          desc: meta.desc,
          path: item.path,
          group,
          icon: item.icon,
          keywords: `${item.label} ${meta.keywords}`,
        };
      });
  }, [isAdmin]);

  const globalSearchResults = useMemo<GlobalSearchItem[]>(() => {
    const keyword = globalSearch.trim();
    const normalized = keyword.toLowerCase();
    if (!keyword) {
      return globalSearchTargets
        .filter(item => ["工作台", "工作流协助", "站群管理", "SEO中心", "GEO中心", "团队协作管理", "API与集成"].includes(item.label))
        .slice(0, 6);
    }

    const scored = globalSearchTargets
      .map(item => {
        const haystack = `${item.label} ${item.desc} ${item.group} ${item.keywords}`.toLowerCase();
        const score = item.label.toLowerCase() === normalized
          ? 100
          : item.label.toLowerCase().includes(normalized)
            ? 70
            : haystack.includes(normalized)
              ? 40
              : 0;
        return { item, score };
      })
      .filter(entry => entry.score > 0)
      .sort((left, right) => right.score - left.score || left.item.label.localeCompare(right.item.label))
      .slice(0, 5)
      .map(entry => entry.item);

    const encoded = encodeURIComponent(keyword);
    const quickSearches: GlobalSearchItem[] = [
      {
        id: `site-query-${encoded}`,
        label: `在站群管理搜索“${keyword}”`,
        desc: "按域名、站点名称、行业或分组查找站点",
        path: `/sites?search=${encoded}`,
        group: "业务搜索",
        icon: SearchCheck,
        keywords: keyword,
      },
      {
        id: `keyword-query-${encoded}`,
        label: `在关键词中心搜索“${keyword}”`,
        desc: "筛选关键词、分类、意图和标题规划",
        path: `/keyword-center?search=${encoded}`,
        group: "业务搜索",
        icon: Target,
        keywords: keyword,
      },
      {
        id: `task-query-${encoded}`,
        label: `在团队协作搜索“${keyword}”`,
        desc: "筛选任务、项目、成员和操作日志",
        path: `/team-collaboration?search=${encoded}`,
        group: "业务搜索",
        icon: ClipboardCheck,
        keywords: keyword,
      },
      {
        id: `seo-query-${encoded}`,
        label: `在SEO诊断搜索“${keyword}”`,
        desc: "进入SEO诊断历史按网站、域名或任务ID检索",
        path: `/seo-diagnosis?search=${encoded}`,
        group: "业务搜索",
        icon: SearchCheck,
        keywords: keyword,
      },
    ];

    const seen = new Set(scored.map(item => item.path));
    return [...scored, ...quickSearches.filter(item => !seen.has(item.path))].slice(0, 8);
  }, [globalSearch, globalSearchTargets]);

  const openGlobalSearchItem = (item: GlobalSearchItem) => {
    handleNavClick(item.path.split("?")[0]);
    navigate(item.path);
    setGlobalSearch("");
    setGlobalSearchOpen(false);
  };

  const renderNextNavItem = (item: SidebarNavItem, nested = false) => {
    const Icon = item.icon;
    const isActive = isRouteActive(item.path, item.aliases);
    const badgeIsNumber = item.badge && /^\d+$/.test(item.badge);

    return (
      <NavLink
        key={`${item.path}-${item.label}`}
        to={item.path}
        onClick={() => handleNavClick(item.path)}
        className={`relative flex items-center rounded-lg transition-all duration-150 ${collapsed ? "h-9 justify-center px-0" : nested ? "ml-5 h-[30px] gap-2 px-2" : "h-[34px] gap-2.5 px-2.5"} hover:bg-white/[0.04]`}
        style={{
          background: isActive ? "#143f3a" : "transparent",
          color: isActive ? "#ffffff" : "#9ca3af",
          boxShadow: "none",
        }}
      >
        {isActive && !collapsed && (
          <span className="absolute -left-3 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-emerald-500" />
        )}
        <Icon
          size={nested ? 15 : 17}
          className="flex-shrink-0"
          style={{ color: isActive ? "#4ade80" : "#9ca3af" }}
        />
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1 truncate text-[13px] font-semibold leading-none">{item.label}</span>
            {item.badge && (
              <span
                className="shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-black leading-none"
                style={{
                  background: badgeIsNumber ? "#ef1235" : "#7c3aed",
                  color: "#ffffff",
                }}
              >
                {item.badge}
              </span>
            )}
            {item.trailingChevron && (
              <ChevronRight size={14} className="shrink-0" style={{ color: "#9ca3af" }} />
            )}
          </>
        )}
      </NavLink>
    );
  };

  const renderNextNav = () => {
    if (collapsed) {
      return (
        <div className="space-y-1">
          {nextItemsForCollapsed.map(item => renderNextNavItem(item))}
        </div>
      );
    }

    return (
      <div className="space-y-1">
        {renderNextNavItem(nextHomeItem)}
        {nextStandaloneItems.filter(item => !item.adminOnly || isAdmin).map(item => renderNextNavItem(item))}
        {nextNavSections.filter(section => section.id !== "system").map(section => {
          const SectionIcon = section.icon;
          const open = openNextSections[section.id] ?? true;
          const visibleItems = section.items.filter(item => !item.adminOnly || isAdmin);
          if (!visibleItems.length) return null;

          return (
            <div key={section.id} className="space-y-0.5">
              <button
                type="button"
                onClick={() => toggleNextSection(section.id)}
                className="flex h-[34px] w-full items-center gap-2.5 rounded-lg px-2.5 text-left transition-colors hover:bg-white/[0.04]"
                style={{ color: "#d1d5db" }}
              >
                <SectionIcon size={17} className="shrink-0" style={{ color: "#9ca3af" }} />
                <span className="min-w-0 flex-1 truncate text-[13px] font-bold leading-none">{section.label}</span>
                <ChevronDown
                  size={14}
                  className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                  style={{ color: "#9ca3af" }}
                />
              </button>
              {open && (
                <div className="space-y-0.5">
                  {visibleItems.map(item => renderNextNavItem(item, true))}
                </div>
              )}
            </div>
          );
        })}
        {nextNavSections.filter(section => section.id === "system").map(section => {
          const SectionIcon = section.icon;
          const open = openNextSections[section.id] ?? true;
          const visibleItems = section.items.filter(item => !item.adminOnly || isAdmin);
          if (!visibleItems.length) return null;

          return (
            <div key={section.id} className="space-y-0.5">
              <button
                type="button"
                onClick={() => toggleNextSection(section.id)}
                className="flex h-[34px] w-full items-center gap-2.5 rounded-lg px-2.5 text-left transition-colors hover:bg-white/[0.04]"
                style={{ color: "#d1d5db" }}
              >
                <SectionIcon size={17} className="shrink-0" style={{ color: "#9ca3af" }} />
                <span className="min-w-0 flex-1 truncate text-[13px] font-bold leading-none">{section.label}</span>
                <ChevronDown
                  size={14}
                  className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                  style={{ color: "#9ca3af" }}
                />
              </button>
              {open && (
                <div className="space-y-0.5">
                  {visibleItems.map(item => renderNextNavItem(item, true))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  useEffect(() => {
    const compactNavigation = window.matchMedia("(max-width: 900px)");
    const syncNavigation = () => setCollapsed(compactNavigation.matches);
    syncNavigation();
    compactNavigation.addEventListener("change", syncNavigation);
    return () => compactNavigation.removeEventListener("change", syncNavigation);
  }, []);

  useEffect(() => {
    const syncSettings = () => setSystemSettings(loadSystemSettings());
    window.addEventListener(SYSTEM_SETTINGS_UPDATED, syncSettings);
    window.addEventListener("storage", syncSettings);
    return () => {
      window.removeEventListener(SYSTEM_SETTINGS_UPDATED, syncSettings);
      window.removeEventListener("storage", syncSettings);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    apiFetch("/admin/settings", { method: "GET", notifyOnError: false })
      .then(response => response.ok ? response.json() : null)
      .then(body => {
        if (cancelled || !body?.ok || !body?.data?.settings) return;
        saveSystemSettings(body.data.settings);
        setSystemSettings(loadSystemSettings());
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "k") return;
      event.preventDefault();
      setGlobalSearchOpen(true);
      globalSearchInputRef.current?.focus();
      globalSearchInputRef.current?.select();
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  useEffect(() => {
    if (!globalSearchOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!globalSearchRef.current?.contains(event.target as Node)) {
        setGlobalSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [globalSearchOpen]);

  const handleNavClick = (path: string) => {
    if (path !== "/site-builder" || typeof window === "undefined") return;
    window.localStorage.removeItem(BUILDER_DOMAIN_DRAFT_STORAGE_KEY);
    window.dispatchEvent(new Event(SITE_BUILDER_RESET_EVENT));
  };

  return (
    <div
      className="seo-dark-shell fixed inset-0 flex overflow-hidden"
      style={{ background: activeShell.page, fontFamily: "var(--font-sans)", color: isDark ? "#f3f4f6" : "#111827" }}
    >
      {/* @section: sidebar */}
      <aside
        className="flex flex-col flex-shrink-0 transition-all duration-300"
        style={{
          width: sidebarWidth,
          background: sidebarPanel,
          borderRight: `1px solid ${sidebarBorder}`,
        }}
      >
        {/* Logo */}
        <div className="flex h-14 items-center gap-3 px-4 border-b" style={{ borderColor: sidebarBorder }}>
          {hasCustomPlatformLogo ? (
            <img src={systemSettings.logoDataUrl} alt={systemSettings.platformName} className={collapsed ? "h-8 w-8 shrink-0 object-contain" : "h-8 max-w-[108px] shrink-0 object-contain"} />
          ) : (
            <LiangmuBrandMark size={32} />
          )}
          {!collapsed && !hasCustomPlatformLogo && (
            <div>
              <div className="text-[13px] font-bold tracking-tight" style={{ color: sidebarPrimaryText }}>{systemSettings.platformName}</div>
              <div className="flex items-center gap-1 text-[11px]" style={{ color: "#4ade80" }}>
                <span className="h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse" />
                AI 协作引擎运行中
              </div>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="ml-auto p-1 rounded-md opacity-50 hover:opacity-100 transition-opacity"
            style={{ color: "#d1d5db" }}
            aria-label={collapsed ? "展开导航" : "收起导航"}
          >
            <ChevronRight size={14} className={`transition-transform duration-200 ${collapsed ? "" : "rotate-180"}`} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 min-h-0 overflow-x-hidden overflow-y-auto px-3 pb-1 pt-2.5">
          {renderNextNav()}
        </nav>

        {/* Bottom */}
        {!collapsed && user?.planId === "free" && (
        <div className="shrink-0 border-t px-3 pb-2 pt-1.5" style={{ borderColor: sidebarBorder }}>
          {/* Upgrade plan */}
          <NavLink to="/pricing"
            className="flex h-7 items-center gap-2 rounded-lg px-3 transition-opacity hover:opacity-90"
            style={{
              background: "rgba(249, 115, 22, 0.14)",
              color: "#fb923c",
            }}>
            <Crown size={14} className="flex-shrink-0" />
            <span className="text-[11px] font-semibold leading-none">升级到 Pro</span>
          </NavLink>
        </div>
        )}
      </aside>

      {/* @section: main-area */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {/* Topbar */}
        <header className="flex h-16 flex-shrink-0 items-center gap-2 border-b px-3 sm:gap-3 sm:px-5"
          style={{ background: activeShell.panel, borderColor: activeShell.border }}>
          {/* Search */}
          <div ref={globalSearchRef} className="relative flex h-11 min-w-0 flex-1 items-center gap-2 rounded-xl border px-3 md:min-w-[17rem] md:flex-[0_1_21rem]"
            style={{ background: activeShell.panel2, borderColor: activeShell.border }}>
            <Search size={14} style={{ color: activeShell.muted }} />
            <input
              ref={globalSearchInputRef}
              value={globalSearch}
              onChange={event => {
                setGlobalSearch(event.target.value);
                setGlobalSearchOpen(true);
              }}
              onFocus={() => setGlobalSearchOpen(true)}
              onKeyDown={event => {
                if (event.key === "Enter" && globalSearchResults[0]) {
                  event.preventDefault();
                  openGlobalSearchItem(globalSearchResults[0]);
                }
                if (event.key === "Escape") {
                  event.preventDefault();
                  if (globalSearch) setGlobalSearch("");
                  setGlobalSearchOpen(false);
                }
              }}
              placeholder="搜索站点、关键词、任务..."
              className="min-w-0 flex-1 truncate bg-transparent text-sm outline-none"
              style={{ color: isDark ? "#fff" : "#111827" }}
            />
            {globalSearch ? (
              <button
                type="button"
                onClick={() => {
                  setGlobalSearch("");
                  globalSearchInputRef.current?.focus();
                  setGlobalSearchOpen(true);
                }}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition hover:bg-white/5"
                style={{ color: "#9ca3af" }}
                title="清空搜索"
              >
                <X size={13} />
              </button>
            ) : null}
            <kbd className="inline-flex h-6 shrink-0 items-center justify-center rounded-md border px-1.5 font-mono text-[11px] leading-none opacity-55"
              style={{ borderColor: activeShell.border, color: "#9ca3af" }}>⌘K</kbd>
            {globalSearchOpen ? (
              <div
                className="absolute left-0 top-[calc(100%+8px)] z-50 w-[min(34rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border p-2 shadow-2xl"
                style={{ background: "#1a2332", borderColor: activeShell.border, boxShadow: "0 24px 80px rgba(0,0,0,.34)" }}
              >
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="text-[11px] font-bold" style={{ color: "#9ca3af" }}>{globalSearch.trim() ? "搜索结果" : "常用入口"}</span>
                  <span className="text-[11px]" style={{ color: "#6b7280" }}>Enter 打开</span>
                </div>
                <div className="max-h-80 overflow-y-auto pr-1">
                  {globalSearchResults.map(item => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onMouseDown={event => {
                          event.preventDefault();
                          openGlobalSearchItem(item);
                        }}
                        className="mb-1 flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition last:mb-0 hover:bg-white/[0.04]"
                        style={{ background: "rgba(15,23,42,.18)", borderColor: "rgba(148,163,184,.08)" }}
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" style={{ background: "rgba(16,185,129,.12)", color: "#4ade80" }}>
                          <Icon size={17} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span className="truncate text-sm font-bold text-white">{item.label}</span>
                            <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: "rgba(148,163,184,.10)", color: "#9ca3af" }}>{item.group}</span>
                          </span>
                          <span className="mt-1 block truncate text-xs" style={{ color: "#9ca3af" }}>{item.desc}</span>
                        </span>
                        <ChevronRight size={15} className="shrink-0" style={{ color: "#6b7280" }} />
                      </button>
                    );
                  })}
                  {!globalSearchResults.length ? (
                    <div className="rounded-xl border px-3 py-6 text-center text-xs" style={{ borderColor: "rgba(148,163,184,.08)", color: "#9ca3af" }}>
                      没找到匹配入口，换个站点、关键词或任务名称试试
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>

          <div className="hidden items-center gap-2 xl:flex">
            <NavLink
              to="/api-center"
              className="h-11 whitespace-nowrap px-3 rounded-lg border flex items-center gap-2 text-sm font-medium transition hover:opacity-90"
              style={{
                background: activeShell.panel2,
                borderColor: activeShell.border,
                color: isDark ? "#e5e7eb" : "#111827",
              }}
            >
              <Plug size={15} style={{ color: "#60a5fa" }} />
              接口中心
            </NavLink>
            <NavLink
              to="/help"
              className="h-11 whitespace-nowrap px-3 rounded-lg border flex items-center gap-2 text-sm font-medium transition hover:opacity-90"
              style={{
                background: activeShell.panel2,
                borderColor: activeShell.border,
                color: isDark ? "#e5e7eb" : "#111827",
              }}
            >
              <HelpCircle size={15} style={{ color: "#a78bfa" }} />
              帮助文档
            </NavLink>
            <NavLink
              to="/resources"
              className="h-11 whitespace-nowrap px-3 rounded-lg border flex items-center gap-2 text-sm font-medium transition hover:opacity-90"
              style={{
                background: location.pathname === "/resources" ? "rgba(13, 112, 70, 0.24)" : activeShell.panel2,
                borderColor: location.pathname === "/resources" ? "rgba(74, 222, 128, 0.42)" : activeShell.border,
                color: isDark ? "#e5e7eb" : "#111827",
              }}
            >
              <Database size={15} style={{ color: "#4ade80" }} />
              资源管理
            </NavLink>
          </div>

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <NavLink
              to="/ai"
              className="hidden h-9 min-w-[108px] items-center justify-center gap-2 whitespace-nowrap rounded-full border px-3 transition hover:opacity-90 lg:flex"
              style={{
                background: "linear-gradient(135deg, rgba(4, 70, 43, 0.95), rgba(5, 83, 50, 0.98))",
                borderColor: "rgba(74, 222, 128, 0.18)",
                color: "#ffffff",
              }}
              title="AI增长总管在线"
            >
              <span className="relative grid h-5 w-5 place-items-center">
                <Bot size={16} />
                <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border border-[#064e3b] bg-emerald-300" />
              </span>
              <span className="text-xs font-bold leading-none">AI总管</span>
              <span className="rounded-full px-2 py-0.5 text-[11px] font-bold leading-none" style={{ background: "rgba(120, 79, 30, 0.85)", color: "#ffffff" }}>在线</span>
            </NavLink>

            {/* AI Credit */}
            <div className="relative hidden items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm cursor-pointer xl:flex"
              style={{ background: activeShell.panel2, color: isDark ? "#f3f4f6" : "#111827" }}>
              <Zap size={12} />
              <span>AI 积分</span>
              <span className="font-bold" style={{ color: "#f97316" }}>4,820</span>
              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold text-white">5</span>
            </div>

            {/* Notification */}
            <Button variant="ghost" size="icon" className="relative rounded-lg" style={{ width: 40, height: 40, background: activeShell.panel2, color: "#9ca3af" }}>
              <Bell size={16} />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold text-white">3</span>
            </Button>

            {/* User */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 whitespace-nowrap pl-3 pr-2 py-1.5 hover:opacity-90 transition border-l"
                  style={{ borderColor: activeShell.border }}>
                  <Avatar className="w-10 h-10">
                    {user?.image ? <AvatarImage src={user.image} alt={userName} /> : null}
                    <AvatarFallback className="text-xs font-bold" style={{ background: "linear-gradient(135deg, #f97316, #ef4444)", color: "white" }}>{userInitial}</AvatarFallback>
                  </Avatar>
                  <span className="hidden whitespace-nowrap text-sm font-medium sm:inline" style={{ color: isDark ? "#ffffff" : "#111827" }}>{userName}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={() => navigate("/system-settings?section=profile")}><User size={14} className="mr-2" />个人资料</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/pricing")}><Crown size={14} className="mr-2" />升级计划</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/system-settings")}><Shield size={14} className="mr-2" />系统设置</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-destructive" onClick={handleLogout}><LogOut size={14} className="mr-2" />退出登录</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="min-h-0 flex-1 overflow-y-auto" style={{ background: activeShell.page }}>
          {children}
        </main>
      </div>
    </div>
  );
}
